/**
 * V17.523 (Phase 1, Rule 7): menu search — the button at the top of the sidebar and Ctrl K / ⌘K
 * open a shadcn Command dialog. It matches part of the Lao or English label, visible items only.
 * Each result row: section icon · label · other-language label · section chip. The empty state
 * is the signed text, and it is the menu's only "no access" hint (Rule 2).
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { NAV_SECTIONS, type NavItem } from '../../app/navigation';
import { Command, CommandEmpty, CommandInput, CommandItem, CommandList } from '../ui/command';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '../ui/dialog';
import { NAV_ICONS } from './icons';
import { searchItems } from './navModel';
import { NAV_STRINGS, type NavLang } from './navStrings';

interface NavSearchDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  items: NavItem[];
  lang: NavLang;
}

/** Ctrl K / ⌘K anywhere opens the search. */
export function useSearchShortcut(onOpen: () => void) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        onOpen();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onOpen]);
}

export function NavSearchDialog({ open, onOpenChange, items, lang }: NavSearchDialogProps) {
  const s = NAV_STRINGS[lang];
  const other: NavLang = lang === 'lo' ? 'en' : 'lo';
  const [query, setQuery] = useState('');
  const navigate = useNavigate();
  const results = useMemo(() => searchItems(query, items), [query, items]);
  const sectionOf = (item: NavItem) => NAV_SECTIONS.find((x) => x.id === item.section)!;

  useEffect(() => {
    if (!open) setQuery('');
  }, [open]);

  const go = (item: NavItem) => {
    onOpenChange(false);
    navigate(item.path);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent closeLabel={s.close} lang={lang} className="font-nav">
        <DialogTitle className="sr-only">{s.search}</DialogTitle>
        <DialogDescription className="sr-only">{s.searchPh}</DialogDescription>
        <Command shouldFilter={false} label={s.search}>
          <CommandInput value={query} onValueChange={setQuery} placeholder={s.searchPh} aria-label={s.search} />
          <p className="px-4 pt-2 text-xs text-sidebar-muted" aria-live="polite">
            {query.trim() ? s.found(results.length) : s.all(items.length)}
          </p>
          <CommandList>
            <CommandEmpty>
              <p className="font-medium text-sidebar-strong">{s.noRes(query.trim())}</p>
              <p className="mt-1 text-sidebar-muted">{s.noResHint}</p>
              <p className="mt-1 text-sidebar-muted">{s.access}</p>
            </CommandEmpty>
            {results.map((item) => {
              const section = sectionOf(item);
              const Icon = NAV_ICONS[section.icon];
              return (
                <CommandItem key={item.id} value={item.id} onSelect={() => go(item)} data-nav-path={item.path}>
                  <Icon className="h-4 w-4 shrink-0 text-sidebar-icon" aria-hidden="true" />
                  <span className="min-w-0 flex-1">
                    <span className="block leading-snug text-sidebar-strong">{item.label[lang]}</span>
                    <span className="block text-xs leading-snug text-sidebar-muted" lang={other}>
                      {item.label[other]}
                    </span>
                  </span>
                  <span className="shrink-0 rounded-full bg-sidebar-chip px-2 py-0.5 text-xs text-sidebar-chip-fg">
                    {section.label[lang]}
                  </span>
                </CommandItem>
              );
            })}
          </CommandList>
          <div className="flex gap-4 border-t border-sidebar-border px-4 py-2 text-xs text-sidebar-muted">
            <span><kbd className="rounded bg-sidebar-kbd px-1">↑↓</kbd> {s.kNav}</span>
            <span><kbd className="rounded bg-sidebar-kbd px-1">↵</kbd> {s.kOpen}</span>
            <span><kbd className="rounded bg-sidebar-kbd px-1">Esc</kbd> {s.kClose}</span>
          </div>
        </Command>
      </DialogContent>
    </Dialog>
  );
}
